import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BOOKMAKER_DISPLAY_NAMES, BookmakerLogo } from './bookmakerAssets';

export const DEFAULT_HOT_PROPS_FILTERS = {
    // movement_threshold has different defaults per filter_type, so we track both.
    movers_movement_threshold: 10,
    edge_movement_threshold: 5,
    edge_threshold: 10,
    movement_bookmaker: null,
    exclude_best_bookmakers: [],
    minimum_odds: null,
};

const MOVERS_MOVEMENT_MIN = 5;
const MOVERS_MOVEMENT_MAX = 30;
const EDGE_MOVEMENT_MIN = 5;
const EDGE_MOVEMENT_MAX = 25;
const EDGE_MIN = 5;
const EDGE_MAX = 25;

const MIN_ODDS_PRESETS = [1.5, 2.0, 3.0, 5.0];

const MODE_OPTIONS = [
    { value: 'most_edge', label: 'Most Edge', copy: 'Slow books lagging the market — where value still sits.' },
    { value: 'market_movers', label: 'Market Movers', copy: 'Props whose implied probability has shifted most.' },
];

function SectionShell({ label, hint, active = false, children }) {
    return (
        <section
            className={`relative rounded-lg border bg-slate-900/40 px-4 pt-3.5 pb-4 transition-colors ${
                active
                    ? 'border-emerald-500/40'
                    : 'border-slate-800/80 hover:border-slate-700/80'
            }`}
        >
            {active && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400"
                />
            )}
            <header className="mb-2.5">
                <h4 className="text-[10px] font-mono font-semibold text-slate-400 uppercase tracking-[0.14em]">
                    {label}
                </h4>
                {hint && (
                    <p className="mt-1 text-[11px] text-slate-500 leading-snug">{hint}</p>
                )}
            </header>
            {children}
        </section>
    );
}

function ThresholdSlider({ value, min, max, step = 0.5, onChange }) {
    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                    Threshold
                </span>
                <span className="flex items-baseline gap-1">
                    <span className="text-xl font-mono font-bold text-emerald-400 tabular-nums leading-none">
                        {Number(value).toFixed(1)}
                    </span>
                    <span className="text-[11px] font-mono text-emerald-500/70 uppercase tracking-wider">
                        %
                    </span>
                </span>
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer accent-emerald-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg
                    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600"
            />

            <div className="flex justify-between mt-1.5 text-[9px] font-mono text-slate-700 uppercase tracking-wider">
                <span>{min}%</span>
                <span>{max}%</span>
            </div>
        </div>
    );
}

function BookmakerChip({ bookmaker, selected, mode = 'toggle', onClick }) {
    const label = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;
    const base = 'group inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-all duration-150';
    const activeStyle =
        mode === 'exclude'
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
            : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
    const idleStyle = 'border-slate-800 bg-slate-900/40 text-slate-400 hover:border-slate-700 hover:text-slate-200';

    return (
        <button
            type="button"
            onClick={() => onClick(bookmaker)}
            className={`${base} ${selected ? activeStyle : idleStyle}`}
        >
            <BookmakerLogo
                bookmaker={bookmaker}
                size={14}
                className={selected ? '' : 'opacity-60 group-hover:opacity-90'}
            />
            <span>{label}</span>
            {selected && mode === 'exclude' && (
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            )}
            {selected && mode === 'toggle' && (
                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                </svg>
            )}
        </button>
    );
}

function HotPropsFiltersModal({
    open,
    filterType,
    filters,
    bookmakers,
    onModeChange,
    onApply,
    onClose,
}) {
    const [localFilters, setLocalFilters] = useState(filters);
    const [localMode, setLocalMode] = useState(filterType);

    useEffect(() => {
        if (open) {
            setLocalFilters(filters);
            setLocalMode(filterType);
        }
    }, [open, filters, filterType]);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    const availableBookmakers = useMemo(() => {
        if (Array.isArray(bookmakers) && bookmakers.length > 0) {
            return [...new Set(bookmakers)].sort();
        }
        return Object.keys(BOOKMAKER_DISPLAY_NAMES).sort();
    }, [bookmakers]);

    const toggleExcludeBook = useCallback((book) => {
        setLocalFilters((prev) => {
            const cur = new Set(prev.exclude_best_bookmakers || []);
            if (cur.has(book)) cur.delete(book); else cur.add(book);
            return { ...prev, exclude_best_bookmakers: Array.from(cur) };
        });
    }, []);

    const setMovementBookmaker = useCallback((book) => {
        setLocalFilters((prev) => ({
            ...prev,
            movement_bookmaker:
                prev.movement_bookmaker === book ? null : book,
        }));
    }, []);

    const setMinimumOdds = useCallback((val) => {
        setLocalFilters((prev) => ({ ...prev, minimum_odds: val }));
    }, []);

    const activeCount = useMemo(() => {
        let n = 0;
        if (localMode === 'market_movers') {
            if (
                localFilters.movers_movement_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.movers_movement_threshold
            ) n++;
        }
        if (localMode === 'most_edge') {
            if (
                localFilters.edge_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.edge_threshold
            ) n++;
            if (
                localFilters.edge_movement_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.edge_movement_threshold
            ) n++;
            if (localFilters.movement_bookmaker) n++;
            if ((localFilters.exclude_best_bookmakers || []).length > 0) n++;
            if (localFilters.minimum_odds != null && localFilters.minimum_odds !== '') n++;
        }
        return n;
    }, [localMode, localFilters]);

    const handleReset = () => {
        setLocalFilters({ ...DEFAULT_HOT_PROPS_FILTERS });
    };

    const handleApply = () => {
        if (localMode !== filterType && typeof onModeChange === 'function') {
            onModeChange(localMode);
        }
        onApply(localFilters);
        onClose();
    };

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 10000 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Hot Props Filters"
        >
            <div
                className="relative w-full md:max-w-xl bg-slate-900 border-t md:border border-slate-800 rounded-t-2xl md:rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[85vh] md:max-h-[90vh] animate-slide-up md:animate-fade-in"
                onClick={(e) => e.stopPropagation()}
                style={{
                    paddingBottom:
                        'max(env(safe-area-inset-bottom, 0px), 0px)',
                }}
            >
                {/* Mobile grab handle */}
                <div
                    aria-hidden="true"
                    className="md:hidden flex justify-center pt-2"
                >
                    <span className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>

                {/* Top emerald hairline + subtle glow */}
                <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-center justify-between px-5 pt-3 md:pt-5 pb-4 border-b border-slate-800/80">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center text-emerald-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-white tracking-tight">Filters</h3>
                            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.14em]">
                                {activeCount > 0 ? `${activeCount} active` : 'Defaults'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close filters"
                        className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto styled-scrollbar px-5 py-5 space-y-4">
                    {/* Mode switcher */}
                    <div>
                        <h4 className="text-[10px] font-mono font-semibold text-slate-400 uppercase tracking-[0.14em] mb-2">
                            Mode
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                            {MODE_OPTIONS.map((opt) => {
                                const selected = localMode === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setLocalMode(opt.value)}
                                        className={`text-left rounded-lg border px-3 py-2.5 transition-all duration-150 ${
                                            selected
                                                ? 'bg-emerald-500/10 border-emerald-500/40'
                                                : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`w-1.5 h-1.5 rounded-full ${
                                                    selected ? 'bg-emerald-400 animate-pulse' : 'bg-slate-700'
                                                }`}
                                            />
                                            <span
                                                className={`text-xs font-semibold ${
                                                    selected ? 'text-emerald-200' : 'text-slate-300'
                                                }`}
                                            >
                                                {opt.label}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-[10px] text-slate-500 leading-snug">
                                            {opt.copy}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Market Movers filters */}
                    {localMode === 'market_movers' && (
                        <SectionShell
                            label="Movement Threshold"
                            hint="Only include props whose biggest-mover reached at least this much implied-probability change."
                            active={
                                localFilters.movers_movement_threshold !==
                                DEFAULT_HOT_PROPS_FILTERS.movers_movement_threshold
                            }
                        >
                            <ThresholdSlider
                                value={localFilters.movers_movement_threshold}
                                min={MOVERS_MOVEMENT_MIN}
                                max={MOVERS_MOVEMENT_MAX}
                                onChange={(v) =>
                                    setLocalFilters((prev) => ({ ...prev, movers_movement_threshold: v }))
                                }
                            />
                        </SectionShell>
                    )}

                    {/* Most Edge filters */}
                    {localMode === 'most_edge' && (
                        <>
                            <SectionShell
                                label="Edge Threshold"
                                hint="Minimum edge between the best available price and the biggest mover."
                                active={
                                    localFilters.edge_threshold !==
                                    DEFAULT_HOT_PROPS_FILTERS.edge_threshold
                                }
                            >
                                <ThresholdSlider
                                    value={localFilters.edge_threshold}
                                    min={EDGE_MIN}
                                    max={EDGE_MAX}
                                    onChange={(v) =>
                                        setLocalFilters((prev) => ({ ...prev, edge_threshold: v }))
                                    }
                                />
                            </SectionShell>

                            <SectionShell
                                label="Movement Floor"
                                hint="Also require the biggest mover to have shifted at least this much. Lower values surface small moves with big edge."
                                active={
                                    localFilters.edge_movement_threshold !==
                                    DEFAULT_HOT_PROPS_FILTERS.edge_movement_threshold
                                }
                            >
                                <ThresholdSlider
                                    value={localFilters.edge_movement_threshold}
                                    min={EDGE_MOVEMENT_MIN}
                                    max={EDGE_MOVEMENT_MAX}
                                    onChange={(v) =>
                                        setLocalFilters((prev) => ({ ...prev, edge_movement_threshold: v }))
                                    }
                                />
                            </SectionShell>

                            <SectionShell
                                label="Movement Bookmaker"
                                hint="Only show props whose biggest mover was this bookmaker."
                                active={!!localFilters.movement_bookmaker}
                            >
                                <div className="flex flex-wrap gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setMovementBookmaker(null)}
                                        className={`inline-flex items-center rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-all ${
                                            !localFilters.movement_bookmaker
                                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                                : 'border-slate-800 bg-slate-900/40 text-slate-500 hover:border-slate-700 hover:text-slate-200'
                                        }`}
                                    >
                                        Any
                                    </button>
                                    {availableBookmakers.map((bk) => (
                                        <BookmakerChip
                                            key={bk}
                                            bookmaker={bk}
                                            mode="toggle"
                                            selected={localFilters.movement_bookmaker === bk}
                                            onClick={setMovementBookmaker}
                                        />
                                    ))}
                                </div>
                            </SectionShell>

                            <SectionShell
                                label="Exclude From Best Odds"
                                hint="Hide props whose best price comes from any selected bookmaker."
                                active={(localFilters.exclude_best_bookmakers || []).length > 0}
                            >
                                <div className="flex flex-wrap gap-1.5">
                                    {availableBookmakers.map((bk) => {
                                        const selected = (localFilters.exclude_best_bookmakers || []).includes(bk);
                                        return (
                                            <BookmakerChip
                                                key={bk}
                                                bookmaker={bk}
                                                mode="exclude"
                                                selected={selected}
                                                onClick={toggleExcludeBook}
                                            />
                                        );
                                    })}
                                </div>
                                {(localFilters.exclude_best_bookmakers || []).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setLocalFilters((p) => ({ ...p, exclude_best_bookmakers: [] }))
                                        }
                                        className="mt-3 text-[10px] font-mono text-slate-500 hover:text-slate-300 uppercase tracking-widest"
                                    >
                                        Clear ({localFilters.exclude_best_bookmakers.length})
                                    </button>
                                )}
                            </SectionShell>

                            <SectionShell
                                label="Minimum Odds"
                                hint="Only include props whose best available price meets this floor."
                                active={localFilters.minimum_odds != null && localFilters.minimum_odds !== ''}
                            >
                                <div className="flex items-center gap-3 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() => setMinimumOdds(null)}
                                        className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-all ${
                                            localFilters.minimum_odds == null || localFilters.minimum_odds === ''
                                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                                : 'border-slate-800 bg-slate-900/40 text-slate-500 hover:border-slate-700 hover:text-slate-200'
                                        }`}
                                    >
                                        Any
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {MIN_ODDS_PRESETS.map((p) => {
                                            const selected = Number(localFilters.minimum_odds) === p;
                                            return (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() => setMinimumOdds(p)}
                                                    className={`rounded-md border px-2.5 py-1.5 text-[11px] font-mono font-semibold tabular-nums transition-all ${
                                                        selected
                                                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                                            : 'border-slate-800 bg-slate-900/40 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                                                    }`}
                                                >
                                                    {p.toFixed(2)}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center gap-2 ml-auto">
                                        <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">
                                            Custom
                                        </span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="1"
                                            placeholder="1.80"
                                            value={
                                                localFilters.minimum_odds == null ||
                                                Number.isNaN(Number(localFilters.minimum_odds))
                                                    ? ''
                                                    : localFilters.minimum_odds
                                            }
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setMinimumOdds(v === '' ? null : Number(v));
                                            }}
                                            className="w-20 px-2 py-1.5 bg-slate-950/60 border border-slate-800 rounded text-xs font-mono text-white text-right tabular-nums focus:outline-none focus:border-emerald-500"
                                        />
                                    </div>
                                </div>
                            </SectionShell>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="relative flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-800/80 bg-slate-950/40">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="text-[11px] font-mono text-slate-500 hover:text-slate-200 uppercase tracking-widest transition-colors py-2 md:py-0"
                    >
                        Reset
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 md:px-3.5 md:py-1.5 text-xs font-medium text-slate-300 rounded-md border border-slate-700/80 hover:bg-slate-800/60 hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="px-4 py-2 md:px-4 md:py-1.5 text-xs font-semibold text-emerald-950 bg-emerald-400 rounded-md hover:bg-emerald-300 transition-colors shadow-[0_0_0_1px_rgba(16,185,129,0.4)]"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default HotPropsFiltersModal;
